import React, { memo, useEffect, useReducer, useCallback } from 'react';
import { FlatList, Clipboard } from 'react-native';
import { Space, withPopUp } from '@redlink/components/atoms';
import {
  FormSection,
  TextInput,
  DropDownButton,
  OverlayInput,
  AddItemButton,
  useAlert,
  usePlan,
} from '@redlink/components/molecules';
import { Form, ImageGroupUploader } from '@redlink/components/organisms';
import { SelectCategoryPage } from '@redlink/components/pages';
import {
  InnerLinkItem,
  PreviewGalleryPostCard,
} from '@redlink/components/muscles';
import { useLocale } from '@redlink/assets/locale';
import { useValues } from '@redlink/assets/values';
import {
  DataEntryModalTemplate,
  LinkEditTemplate,
} from '@redlink/components/templates';
import {
  useMutation,
  useQuery,
  generateNewShortLinkQuery,
  newPostQuery,
  editPostQuery,
  getPageProfileQuery,
} from '@redlink/services';
import { initialState, reducer, types } from './store';

export const PostEditPage = withPopUp(
  memo(({ pageName, onSubmit, onDismiss, data }) => {
    const { dictionary } = useLocale();
    const { getPageLink, staticData } = useValues();
    const alert = useAlert();
    const { showPlanDialog } = usePlan();

    const [
      {
        id,
        title,
        token,
        description,
        images,
        isEditMode,
        categoriesIsVisible,
        category,
        links,
        linksIsVisible,
        selectedLink,
      },
      dispatch,
    ] = useReducer(reducer, initialState(data));

    const [newShortLink, { loading: newShortLinkLoading }] = useMutation(
      generateNewShortLinkQuery,
      {
        done: ({ GenerateNewShortLink: { token: newToken } }) =>
          dispatch({ type: types.SET_TOKEN, payload: newToken }),
      }
    );

    useEffect(() => {
      try {
        !isEditMode && newShortLink();
      } catch {}
    }, []);

    const [submitPost, { loading: submitPostIsLoading }] = useMutation(
      !isEditMode ? newPostQuery : editPostQuery,
      {
        done: () => onSubmit(isEditMode),
        limited: ({ maxCount }) => {
          onDismiss && onDismiss();
          showPlanDialog();
        },
      }
    );

    const renderInnerLinkItem = useCallback(
      ({ item, index }) => {
        return (
          <>
            <InnerLinkItem
              {...{ item, pageName }}
              onCopyPressed={() => {
                Clipboard.setString(item.shortLinkUrl);
                alert.show(dictionary.copiedInClipboard, { type: 'info' });
              }}
              onDeletePressed={() => {
                dispatch({
                  type: types.SET_LINKS,
                  payload: [
                    ...links.slice(0, index),
                    ...links.slice(index + 1),
                  ],
                });
              }}
              onEditPressed={() => {
                dispatch({
                  type: types.SET_MULTI,
                  payload: {
                    linksIsVisible: !linksIsVisible,
                    selectedLink: { ...item, index },
                  },
                });
              }}
            />
            <Space size={4} />
          </>
        );
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [links, linksIsVisible, pageName]
    );

    const {
      data: { PageProfile: { currentPlan } = {} },
    } = useQuery(getPageProfileQuery);

    return (
      <DataEntryModalTemplate>
        <Form
          isBusy={newShortLinkLoading || submitPostIsLoading}
          childPageIsVisible={categoriesIsVisible || linksIsVisible}
          actions={[
            {
              title: dictionary.cancel,
              variant: 'secondary',
              onPress: onDismiss,
            },
            {
              title: dictionary.add,
              variant: 'primary',
              onPress: () => {
                submitPost({
                  id,
                  title,
                  images: images.map(({ id }) => id),
                  shortLink: token,
                  category: category?.id,
                  description,
                  links: links.map(({ id, logoName, title, token, url }) => ({
                    id,
                    logoName,
                    title,
                    url,
                    shortLink: token,
                  })),
                });
              },
            },
          ]}
        >
          {(containerLayout) => (
            <FormSection title={dictionary.newPost}>
              <OverlayInput
                {...{ containerLayout }}
                isVisible={categoriesIsVisible}
                renderButton={() => (
                  <DropDownButton
                    require
                    text={category?.title}
                    placeholder={dictionary.category}
                    onPress={() =>
                      dispatch({
                        type: types.SET_CATEGORIES_IS_VISIBLE,
                        payload: !categoriesIsVisible,
                      })
                    }
                  />
                )}
              >
                <SelectCategoryPage
                  {...{ pageName }}
                  type={staticData.categoryType.post}
                  initialSelectedItem={category}
                  onDismiss={(item) =>
                    dispatch({
                      type: types.SET_MULTI,
                      payload: { categoriesIsVisible: false, category: item },
                    })
                  }
                />
              </OverlayInput>
              <Space />
              <ImageGroupUploader
                require
                {...{ images }}
                title={dictionary.images}
                onChange={(item) => {
                  dispatch({
                    type: types.SET_IMAGES,
                    payload: item,
                  });
                }}
              />
              <Space />
              <TextInput
                require
                title={dictionary.title}
                value={title}
                onChangeText={(value) => {
                  dispatch({ type: types.SET_TITLE, payload: value });
                }}
              />
              <Space />
              <TextInput
                title={dictionary.description}
                value={description}
                onChangeText={(value) =>
                  dispatch({ type: types.SET_DESCRIPTION, payload: value })
                }
              />
              <Space />
              <OverlayInput
                {...{ containerLayout }}
                isVisible={linksIsVisible}
                renderButton={() => (
                  <AddItemButton
                    placehoder={dictionary.newLink}
                    onPress={() =>
                      dispatch({
                        type: types.SET_MULTI,
                        payload: {
                          linksIsVisible: !linksIsVisible,
                          selectedLink: null,
                        },
                      })
                    }
                  />
                )}
              >
                <LinkEditTemplate
                  style={{ padding: 0, borderRadius: 0 }}
                  {...(selectedLink && { data: selectedLink })}
                  {...{ pageName }}
                  onDismiss={() =>
                    dispatch({
                      type: types.SET_LINKS_IS_VISIBLE,
                      payload: false,
                    })
                  }
                  onSubmit={({ index, ...item }) => {
                    dispatch({
                      type: types.SET_MULTI,
                      payload: {
                        linksIsVisible: false,
                        selectedLink: item,
                        ...(index === undefined && {
                          links: [...links, item],
                        }),
                        ...(index !== undefined && {
                          links: [
                            ...links.slice(0, index),
                            item,
                            ...links.slice(index + 1),
                          ],
                        }),
                      },
                    });
                  }}
                />
              </OverlayInput>
              <Space size={8} />
              <FlatList data={links} renderItem={renderInnerLinkItem} />
              <Space />
              <TextInput
                editable={currentPlan.isProUser}
                withCrownIcon
                {...(!currentPlan.isProUser && {
                  description: dictionary.basicLimitationHintDescription,
                })}
                require
                en
                title={dictionary.shortLink}
                prefix={`${getPageLink(pageName)}/`}
                value={token}
                onChangeText={(value) =>
                  dispatch({ type: types.SET_TOKEN, payload: value })
                }
              />
            </FormSection>
          )}
        </Form>
        <PreviewGalleryPostCard
          item={{
            images,
            title,
            token,
            category,
            links,
          }}
        />
      </DataEntryModalTemplate>
    );
  })
);
